<section class="reports mt-3">
  <div class="d-flex justify-content-between align-items-center mb-2">
    <h2>{{ 'AMAZON_FINANCE_REPORTS' | translate }}</h2>
    <button class="btn btn-outline-primary" type="button" [disabled]="loading || recoveryBusy" (click)="load()">
      {{ 'REFRESH' | translate }}
    </button>
  </div>
  <p class="text-muted">{{ 'REPORTS_SCOPE' | translate }}</p>
  @if (error) {
    <div class="alert alert-danger" role="alert">{{ error | translate }}</div>
  }
  <section class="border rounded p-3 mb-3" aria-live="polite">
    <h3>{{ 'REPORT_BACKLOG_RESET_TITLE' | translate }}</h3>
    <p>{{ 'REPORT_BACKLOG_RESET_HINT' | translate }}</p>
    <button type="button" class="btn btn-outline-primary" [disabled]="recoveryBusy" (click)="previewBacklog()">
      {{ 'REPORT_BACKLOG_RESET_PREVIEW' | translate }}
    </button>
    @if (backlogPreview; as preview) {
      <p class="mt-2">{{ 'REPORT_BACKLOG_RESET_COUNTS' | translate: preview }}</p>
      <button type="button" class="btn btn-primary" [disabled]="recoveryBusy || preview.eligible === 0" (click)="resetBacklog()">
        {{ 'REPORT_BACKLOG_RESET_CONFIRM' | translate }}
      </button>
      <button type="button" class="btn btn-link" [disabled]="recoveryBusy" (click)="backlogPreview = null">
        {{ 'REPORT_RESET_CANCEL' | translate }}
      </button>
    }
    @if (backlogResult; as result) {
      <p class="mt-2">{{ 'REPORT_RESET_DONE' | translate: result }}</p>
      @if (result.reset > 0) {
        <p>{{ 'REPORT_RESET_RUN_HINT' | translate }}</p>
        <button type="button" class="btn btn-primary" [disabled]="recoveryBusy || mappingSubmitted" (click)="runMapping()">
          {{ 'REPORT_RESET_RUN' | translate }}
        </button>
      }
      @if (mappingSubmitted) {
        <p>{{ 'REPORT_RESET_SUBMITTED' | translate }}</p>
      }
    }
  </section>
  <div class="table-responsive">
    <table class="table table-hover">
      <thead>
        <tr>
          <th>{{ 'REPORT_STARTED' | translate }}</th>
          <th>{{ 'REPORT_STATUS' | translate }}</th>
          <th>{{ 'REPORT_DURATION' | translate }}</th>
          <th>{{ 'REPORT_STARTED_BY' | translate }}</th>
          <th>{{ 'REPORT_ATTEMPTED' | translate }}</th>
          <th>{{ 'REPORT_RESOLVED' | translate }}</th>
          <th>{{ 'REPORT_BLOCKED' | translate }}</th>
        </tr>
      </thead>
      <tbody>
        @for (report of reports; track report.id) {
          <tr [class.table-active]="selected?.id === report.id">
            <td>
              <button type="button" class="btn btn-link p-0" [disabled]="recoveryBusy" (click)="open(report.id)">
                {{ report.startedAt | date: 'yyyy-MM-dd HH:mm:ss' }}
              </button>
            </td>
            <td>{{ report.status }}</td>
            <td>{{ report.finishedAt ? duration(report.durationMillis) : '—' }}</td>
            <td>{{ report.requestedBy || '—' }}</td>
            <td>{{ report.finishedAt ? report.attempted : '—' }}</td>
            <td>{{ report.finishedAt ? report.resolved : '—' }}</td>
            <td>{{ report.finishedAt ? report.blocked : '—' }}</td>
          </tr>
        } @empty {
          <tr>
            <td colspan="7">{{ (loading ? 'REPORT_LOADING' : 'REPORTS_EMPTY') | translate }}</td>
          </tr>
        }
      </tbody>
    </table>
  </div>
  <div class="d-flex gap-2 mb-4">
    <button class="btn btn-outline-secondary" type="button" [disabled]="loading || page === 0" (click)="load(page - 1)">
      {{ 'REPORT_PREVIOUS' | translate }}
    </button>
    <button
      class="btn btn-outline-secondary"
      type="button"
      [disabled]="loading || reports.length < 25"
      (click)="load(page + 1)"
    >
      {{ 'REPORT_NEXT' | translate }}
    </button>
  </div>
  @if (detailLoading) {
    <p aria-live="polite">{{ 'REPORT_LOADING' | translate }}</p>
  }
  @if (selected; as report) {
    <article class="card p-3">
      <h3>{{ report.startedAt | date: 'yyyy-MM-dd HH:mm:ss' }} · {{ report.stage }}</h3>
      @if (report.status === 'RUNNING') {
        <p class="alert alert-info">{{ 'REPORT_RUNNING' | translate }}</p>
      } @else {
        <p>
          {{ 'REPORT_SELECTED' | translate }}: <strong>{{ report.selected }}</strong> ·
          {{ 'REPORT_ATTEMPTED' | translate }}: <strong>{{ report.attempted }}</strong> ·
          {{ 'REPORT_RESOLVED' | translate }}: <strong>{{ report.resolved }}</strong> ·
          {{ 'REPORT_BLOCKED' | translate }}: <strong>{{ report.blocked }}</strong>
        </p>
        <small class="text-muted">{{ 'REPORT_RESOLVED_HINT' | translate }}</small>
        @if (report.error) {
          <p class="alert alert-danger">{{ report.error }}</p>
        }
        <h4 class="mt-3">{{ 'REPORT_TRANSITIONS' | translate }}</h4>
        <ul>
          @for (entry of report.transitions | keyvalue; track entry.key) {
            <li>
              <code>{{ entry.key }}</code
              >: {{ entry.value }}
            </li>
          }
        </ul>
        @if (report.recovery; as recovery) {
          <h4>{{ 'REPORT_RECOVERY' | translate }}</h4>
          <p>
            {{ recovery.initialFailedOrders }} → {{ recovery.remainingFailedOrders }}
            {{ 'REPORT_FAILED_ORDERS' | translate }} · {{ 'REPORT_STOP_' + recovery.stopReason | translate }}
          </p>
          <ul>
            @for (pass of recovery.passes; track pass.number) {
              <li>
                {{ 'REPORT_PASS' | translate }} {{ pass.number }}: {{ pass.ordersBefore }} → {{ pass.ordersAfter }} ·
                {{ pass.attemptedTransactions }} {{ 'REPORT_ATTEMPTED' | translate }}
              </li>
            }
          </ul>
          <small class="text-muted">{{ 'REPORT_RECOVERY_HINT' | translate }}</small>
        }
        <h4 class="mt-3">
          {{ 'REPORT_BLOCKERS' | translate }} ({{ report.blockers.length }}/{{ report.blockerGroups }})
        </h4>
        @if (report.afterCounts['EXCEPTION'] > 0 || report.afterCounts['MAPPED_MISSING_MAPPINGS'] > 0) {
          <section class="border rounded p-3 my-2" aria-live="polite">
            <h5>{{ 'REPORT_RESET_TITLE' | translate }}</h5>
            <label for="report-reset-status">{{ 'REPORT_STATUS' | translate }}</label>
            <select id="report-reset-status" class="form-select mb-2" [(ngModel)]="resetStatus" [disabled]="recoveryBusy" (ngModelChange)="resetSelectionChanged()">
              @if (report.afterCounts['EXCEPTION'] > 0) {
                <option value="EXCEPTION">{{ 'REPORT_EXCEPTIONS' | translate }} ({{ report.afterCounts['EXCEPTION'] }})</option>
              }
              @if (report.afterCounts['MAPPED_MISSING_MAPPINGS'] > 0) {
                <option value="MAPPED_MISSING_MAPPINGS">{{ 'REPORT_RESET_MISSING_MAPPINGS' | translate }} ({{ report.afterCounts['MAPPED_MISSING_MAPPINGS'] }})</option>
              }
            </select>
            <p>{{ 'REPORT_RESET_HINT' | translate }}</p>
            <button type="button" class="btn btn-outline-primary" [disabled]="recoveryBusy" (click)="previewReset()">
              {{ 'REPORT_RESET_PREVIEW' | translate }}
            </button>
            @if (resetPreview; as preview) {
              <p class="mt-2">{{ 'REPORT_RESET_COUNTS' | translate: preview }}</p>
              <button type="button" class="btn btn-primary" [disabled]="recoveryBusy || preview.eligible === 0" (click)="resetMappings()">
                {{ 'REPORT_RESET_CONFIRM' | translate }}
              </button>
              <button type="button" class="btn btn-link" [disabled]="recoveryBusy" (click)="resetPreview = null">
                {{ 'REPORT_RESET_CANCEL' | translate }}
              </button>
            }
            @if (resetResult; as result) {
              <p class="mt-2">{{ 'REPORT_RESET_DONE' | translate: result }}</p>
              @if (result.reset > 0) {
                <p>{{ 'REPORT_RESET_RUN_HINT' | translate }}</p>
                <button type="button" class="btn btn-primary" [disabled]="recoveryBusy || mappingSubmitted" (click)="runMapping()">
                  {{ 'REPORT_RESET_RUN' | translate }}
                </button>
              }
            }
            @if (mappingSubmitted) {
              <p>{{ 'REPORT_RESET_SUBMITTED' | translate }}</p>
            }
          </section>
        }
        @for (blocker of report.blockers; track $index) {
          <details class="border-bottom py-2">
            <summary>
              <strong>{{ blocker.transactions }}</strong> · {{ blocker.status }} · {{ blocker.key }}
            </summary>
            <ul>
              @for (id of blocker.examples; track id) {
                <li>
                  <code>{{ id }}</code>
                </li>
              }
            </ul>
          </details>
        }
        <h4 class="mt-3">{{ 'REPORT_OUTCOMES' | translate }}</h4>
        <label for="report-status">{{ 'REPORT_STATUS' | translate }}</label>
        <select id="report-status" class="form-select mb-2" [(ngModel)]="status" (ngModelChange)="loadOutcomes(0)">
          <option value="">{{ 'REPORT_ALL_STATUSES' | translate }}</option>
          @for (entry of report.afterCounts | keyvalue; track entry.key) {
            <option [value]="entry.key">{{ entry.key }} ({{ entry.value }})</option>
          }
        </select>
        @if (outcomes; as results) {
          <p>{{ results.total }} {{ 'REPORT_TRANSACTIONS' | translate }}</p>
          <div class="table-responsive">
            <table class="table">
              <thead>
                <tr>
                  <th>{{ 'REPORT_TRANSACTION' | translate }}</th>
                  <th>{{ 'REPORT_TRANSITIONS' | translate }}</th>
                  <th>{{ 'REPORT_REFERENCES' | translate }}</th>
                  <th>{{ 'REPORT_DETAILS' | translate }}</th>
                </tr>
              </thead>
              <tbody>
                @for (outcome of results.items; track outcome.transactionId) {
                  <tr>
                    <td>
                      <code>{{ outcome.transactionId }}</code>
                    </td>
                    <td>{{ outcome.beforeStatus }} → {{ outcome.afterStatus }}</td>
                    <td>
                      {{ outcome.marketplace }}<br />{{ 'REPORT_ORDER' | translate }}: {{ outcome.orderId || '—'
                      }}<br />{{ 'REPORT_CUSTOMER' | translate }}: {{ outcome.customerId || '—' }}
                    </td>
                    <td>
                      {{ outcome.error }}
                      @for (mapping of outcome.missingMappings; track mapping) {
                        <div>
                          <code>{{ mapping }}</code>
                        </div>
                      }
                    </td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
          <div class="d-flex gap-2">
            <button
              type="button"
              class="btn btn-outline-secondary"
              [disabled]="outcomePage === 0"
              (click)="loadOutcomes(outcomePage - 1)"
            >
              {{ 'REPORT_PREVIOUS' | translate }}
            </button>
            <button
              type="button"
              class="btn btn-outline-secondary"
              [disabled]="(outcomePage + 1) * results.size >= results.total"
              (click)="loadOutcomes(outcomePage + 1)"
            >
              {{ 'REPORT_NEXT' | translate }}
            </button>
          </div>
        } @else if (!error) {
          <p>{{ 'REPORT_LOADING' | translate }}</p>
        }
      }
    </article>
  }
</section>
